import {
  ReservationFormState,
  ReservationNoteDraft,
  ReservationPartyDraft,
  ReservationPartyFindOrCreateRequest,
  ReservationPartyLookupDto,
  CreateReservationCommand,
  CreateReservationResponse,
  UpdateReservationCommand,
} from '../Types/reservationTypes';
import { OfferOptionsMapping } from '../../OfferOptionsMappings/Types/offerOptionsMappingsTypes';
import { toMappingIds } from './offerOptionsUtils';
import {
  DISCOUNT_AMOUNT_MIN_THRESHOLD,
  MONEY_AMOUNT_MIN_THRESHOLD,
} from '../Constants/reservationConstants';

/**
 * Helper for submitting a reservation.
 * Accepts mutation functions, form state, and additional functions.
 * Calls create/update depending on the current state.
 */
export async function submitReservationForm({
  data,
  formData,
  createdReservationId,
  setCreatedReservationId,
  createReservation,
  updateReservation,
  findOrCreateReservationParty,
  omitPaymentFieldsOnUpdate = false,
  offerOptionsMappings = [],
  offerOptionsMappingsLoading = false,
}: {
  data: Partial<ReservationFormState>;
  formData: ReservationFormState;
  createdReservationId: number | null;
  setCreatedReservationId: (id: number) => void;
  createReservation: (payload: CreateReservationCommand) => Promise<CreateReservationResponse>;
  updateReservation: (payload: { id: number; data: UpdateReservationCommand }) => Promise<unknown>;
  findOrCreateReservationParty?: (
    payload: ReservationPartyFindOrCreateRequest
  ) => Promise<ReservationPartyLookupDto>;
  omitPaymentFieldsOnUpdate?: boolean;
  offerOptionsMappings?: OfferOptionsMapping[];
  offerOptionsMappingsLoading?: boolean;
}) {
  const currentFormData: ReservationFormState = {
    ...formData,
    ...data,
  };
  const selectedOfferOptionsCount = currentFormData.selectedOfferOptions?.length ?? 0;
  let resolvedMappingIds: number[] = [];

  if (selectedOfferOptionsCount > 0) {
    if (offerOptionsMappingsLoading) {
      const loadingError = new Error('Opcje oferty są jeszcze ładowane. Poczekaj chwilę i spróbuj ponownie.');
      Object.assign(loadingError, { status: 400, data: { message: loadingError.message } });
      throw loadingError;
    }

    if ((offerOptionsMappings?.length ?? 0) === 0) {
      const missingError = new Error('Nie udało się pobrać konfiguracji opcji oferty. Odśwież stronę i spróbuj ponownie.');
      Object.assign(missingError, { status: 400, data: { message: missingError.message } });
      throw missingError;
    }

    resolvedMappingIds = toMappingIds(currentFormData.selectedOfferOptions!, offerOptionsMappings);
    if (resolvedMappingIds.length !== selectedOfferOptionsCount) {
      const configError = new Error('Nie wszystkie wybrane opcje oferty mają prawidłową konfigurację. Odśwież stronę i spróbuj ponownie.');
      Object.assign(configError, {
        status: 400,
        data: {
          message: configError.message,
          errors: { SelectedOfferOptions: ['Nie wszystkie wybrane opcje oferty mają prawidłową konfigurację.'] },
        },
      });
      throw configError;
    }
  }

  const normalizedTotalPriceOverride = normalizeTotalPriceOverride(currentFormData.totalPriceOverride);
  const normalizedDiscountAmount = normalizeDiscountAmount(currentFormData.discountAmount);

  const normalizedOfferBoardingPlaceId = Number(currentFormData.offerBoardingPlaceId);
  const participantsCount = Array.isArray(currentFormData.participants) ? currentFormData.participants.length : 0;
  const declaredParticipantsCount = Number(currentFormData.declaredParticipantsCount);
  const normalizedDeclaredParticipantsCount =
    participantsCount > 0
      ? participantsCount
      : Number.isFinite(declaredParticipantsCount) && declaredParticipantsCount > 0
        ? declaredParticipantsCount
        : undefined;
  const normalizedApplicantPartyId =
    Number.isFinite(Number(currentFormData.applicantPartyId)) && Number(currentFormData.applicantPartyId) > 0
      ? Number(currentFormData.applicantPartyId)
      : undefined;
  const normalizedInvoiceBuyerPartyId =
    Number.isFinite(Number(currentFormData.invoiceBuyerPartyId)) &&
    Number(currentFormData.invoiceBuyerPartyId) > 0
      ? Number(currentFormData.invoiceBuyerPartyId)
      : undefined;
  const normalizedFundingPartyId =
    Number.isFinite(Number(currentFormData.fundingPartyId)) && Number(currentFormData.fundingPartyId) > 0
      ? Number(currentFormData.fundingPartyId)
      : undefined;
  const normalizedApplicantId = Number(currentFormData.applicantId);
  const hasLegacyApplicantId =
    currentFormData.applicantId !== '__new__' &&
    Number.isFinite(normalizedApplicantId) &&
    normalizedApplicantId > 0;
  const resolvedPaymentMethod = currentFormData.paymentMethod ?? formData.paymentMethod;
  const shouldOmitPaymentFieldsOnUpdate = omitPaymentFieldsOnUpdate && createdReservationId !== null;

  if (!shouldOmitPaymentFieldsOnUpdate && !resolvedPaymentMethod) {
    throw createFieldValidationError('PaymentMethod', 'Metoda płatności jest wymagana.');
  }

  const [resolvedApplicantPartyId, resolvedInvoiceBuyerPartyId, resolvedFundingPartyId] =
    await Promise.all([
      resolveReservationPartyId({
        selectedValue: currentFormData.applicantPartyId,
        numericValue: normalizedApplicantPartyId,
        draft: currentFormData.applicantPartyDraft,
        findOrCreateReservationParty,
      }),
      resolveReservationPartyId({
        selectedValue: currentFormData.invoiceBuyerPartyId,
        numericValue: normalizedInvoiceBuyerPartyId,
        draft: currentFormData.invoiceBuyerPartyDraft,
        findOrCreateReservationParty,
      }),
      resolveReservationPartyId({
        selectedValue: currentFormData.fundingPartyId,
        numericValue: normalizedFundingPartyId,
        draft: currentFormData.fundingPartyDraft,
        findOrCreateReservationParty,
      }),
  ]);

  if (!resolvedApplicantPartyId) {
    throw createFieldValidationError('ApplicantPartyId', 'Strona zgłaszającego jest wymagana.');
  }

  const notesForWrite = mapNoteDraftsToRequest(currentFormData.notes);

  const basePayload = {
    bookingId: Number(currentFormData.bookingId),
    applicantPartyId: resolvedApplicantPartyId,
    invoiceBuyerPartyId: resolvedInvoiceBuyerPartyId ?? resolvedApplicantPartyId,
    fundingPartyId: resolvedFundingPartyId,
    declaredParticipantsCount: normalizedDeclaredParticipantsCount,
    participants: currentFormData.participants ?? [],
    source: currentFormData.source,
    totalPriceOverride: normalizedTotalPriceOverride ?? null,
    discountAmount: normalizedTotalPriceOverride !== null ? null : normalizedDiscountAmount,
    offerBoardingPlaceId:
      Number.isFinite(normalizedOfferBoardingPlaceId) && normalizedOfferBoardingPlaceId > 0
        ? normalizedOfferBoardingPlaceId
        : undefined,
    selectedAdditionalOptionMappingIds: resolvedMappingIds,
    ...(hasLegacyApplicantId ? { applicantId: normalizedApplicantId } : {}),
  };
  const updatePayload: UpdateReservationCommand = {
    ...basePayload,
    paymentMethod: resolvedPaymentMethod,
    paidAmount: currentFormData.paidAmount ?? 0,
    nonCashPaymentAmount: currentFormData.nonCashPaymentAmount ?? 0,
    paidAt: currentFormData.paidAt,
    paymentComment: currentFormData.paymentComment,
    notes: notesForWrite,
  };
  const resolvedUpdatePayload = shouldOmitPaymentFieldsOnUpdate
    ? { ...basePayload, notes: notesForWrite }
    : updatePayload;

  if (createdReservationId !== null) {
    await updateReservation({
      id: createdReservationId,
      data: resolvedUpdatePayload,
    });
    return { reservationId: createdReservationId };
  }

  const createPayload: CreateReservationCommand = {
    ...basePayload,
    paymentMethod: resolvedPaymentMethod,
    paidAmount: currentFormData.paidAmount ?? 0,
    nonCashPaymentAmount: currentFormData.nonCashPaymentAmount ?? 0,
    paidAt: currentFormData.paidAt,
    paymentComment: currentFormData.paymentComment,
    notes: notesForWrite,
  };

  const createResult = await createReservation(createPayload);
  const newReservationId = createResult?.reservationId;

  if (!Number.isFinite(newReservationId) || newReservationId <= 0) {
    throw createFieldValidationError(
      'ReservationId',
      'Nie udało się utworzyć rezerwacji. Spróbuj ponownie.'
    );
  }

  setCreatedReservationId(newReservationId);
  // Omit `notes` entirely on the automatic follow-up update: the create call above already
  // persisted them, and resending would duplicate every draft (backend treats id<=0 as new).
  const { notes: _createdNotes, ...followUpUpdatePayload } = resolvedUpdatePayload;
  await updateReservation({
    id: newReservationId,
    data: followUpUpdatePayload,
  });
  return { reservationId: newReservationId };
}

function mapNoteDraftsToRequest(
  notes: ReservationNoteDraft[] | undefined
): ReservationNoteDraft[] | undefined {
  if (!notes || notes.length === 0) {
    return undefined;
  }

  return notes.map((note) => ({
    content: note.content,
    ...(note.id ? { id: note.id } : {}),
  }));
}

async function resolveReservationPartyId({
  selectedValue,
  numericValue,
  draft,
  findOrCreateReservationParty,
}: {
  selectedValue: unknown;
  numericValue?: number;
  draft?: ReservationPartyDraft;
  findOrCreateReservationParty?: (
    payload: ReservationPartyFindOrCreateRequest
  ) => Promise<ReservationPartyLookupDto>;
}): Promise<number | undefined> {
  if (numericValue) {
    return numericValue;
  }

  if (selectedValue !== '__new__') {
    return undefined;
  }

  if (!findOrCreateReservationParty) {
    throw new Error('Funkcja findOrCreateReservationParty jest wymagana przy tworzeniu nowej strony rezerwacji.');
  }

  const payload = mapDraftToFindOrCreateRequest(draft);
  const result = await findOrCreateReservationParty(payload);
  return result.id;
}

function mapDraftToFindOrCreateRequest(
  draft?: ReservationPartyDraft
): ReservationPartyFindOrCreateRequest {
  const partyType = draft?.partyType;
  if (partyType !== 'individual' && partyType !== 'organization') {
    throw new Error('Typ strony jest wymagany podczas wyszukiwania lub tworzenia strony rezerwacji.');
  }

  if (!draft) {
    throw new Error('Szkic strony jest wymagany podczas wyszukiwania lub tworzenia strony rezerwacji.');
  }

  if (partyType === 'organization') {
    return {
      partyType,
      organizationName: normalizeString(draft.organizationName),
      taxId: normalizeString(draft.taxId),
      regon: normalizeString(draft.regon),
      email: normalizeString(draft.email),
      phone: normalizeString(draft.phone),
      street: normalizeString(draft.street),
      city: normalizeString(draft.city),
      postalCode: normalizeString(draft.postalCode),
      countryCode: normalizeString(draft.countryCode),
    };
  }

  return {
    partyType,
    firstName: normalizeString(draft.firstName),
    lastName: normalizeString(draft.lastName),
    email: normalizeString(draft.email),
    phone: normalizeString(draft.phone),
    street: normalizeString(draft.street),
    city: normalizeString(draft.city),
    postalCode: normalizeString(draft.postalCode),
    countryCode: normalizeString(draft.countryCode),
  };
}

function normalizeString(value?: string): string | undefined {
  const normalized = value?.trim();
  return normalized ? normalized : undefined;
}

function normalizeTotalPriceOverride(value: unknown): number | null {
  if (value === '' || value === null || value === undefined) {
    return null;
  }

  const normalized = Number(value);
  if (
    Number.isFinite(normalized) &&
    normalized > 0 &&
    normalized < MONEY_AMOUNT_MIN_THRESHOLD
  ) {
    throw createFieldValidationError(
      'TotalPriceOverride',
      'Cena całkowita (grupowa) musi wynosić co najmniej 0,01.'
    );
  }
  return Number.isFinite(normalized) && normalized >= MONEY_AMOUNT_MIN_THRESHOLD
    ? normalized
    : null;
}

function normalizeDiscountAmount(value: unknown): number | null {
  if (value === '' || value === null || value === undefined) return null;
  const normalized = Number(value);
  if (!Number.isFinite(normalized) || normalized < DISCOUNT_AMOUNT_MIN_THRESHOLD) return null;
  return normalized;
}

function createFieldValidationError(fieldName: string, message: string): Error {
  const validationError = new Error(message);
  Object.assign(validationError, {
    status: 400,
    data: {
      message,
      errors: {
        [fieldName]: [message],
      },
    },
  });
  return validationError;
}
